import {
	BookOpen,
	Box,
	Circle,
	ClipboardList,
	Copy,
	Download,
	Edit,
	Folder,
	MoreVertical,
	Share2,
	Star,
	Trash2,
	Users,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { SelectButton } from '@/components/ui/SelectButton'
import type { ContentType } from '@/types/Content.ts'
import './content-list-row.css'
import React from 'react'

const typeIcons: Record<ContentType, React.ComponentType<{
	size?: number;
	className?: string;
	strokeWidth?: number
}>> = {
	playbook: BookOpen,
	document: ClipboardList,
	folder: Folder,
	play: Circle,
	formation: Users,
	concept: Box,
}

type ContentListHeaderProps = {
	hasSelectAll?: boolean
	allSelected?: boolean
	someSelected?: boolean
	onSelectAll?: () => void
}

export function ContentListHeader({
									  hasSelectAll,
									  allSelected,
									  someSelected,
									  onSelectAll,
								  }: ContentListHeaderProps) {
	return (
		<div className="content-list-header">
			<div className="content-list-header-cell" style={{ gridColumn: 'span 1' }}>
				{hasSelectAll && onSelectAll && (
					<SelectButton
						selected={allSelected}
						partial={someSelected}
						onClick={onSelectAll}
					/>
				)}
			</div>
			<div className="content-list-header-cell" style={{ gridColumn: 'span 5' }}>
				Name
			</div>
			<div className="content-list-header-cell" style={{ gridColumn: 'span 2' }}>
				Type
			</div>
			<div className="content-list-header-cell" style={{ gridColumn: 'span 2' }}>
				Items
			</div>
			<div className="content-list-header-cell" style={{ gridColumn: 'span 2' }}>
				Modified
			</div>
			<div className="content-list-header-cell" style={{ gridColumn: 'span 1' }}></div>
		</div>
	)
}

type ContentListRowProps = {
	id: string
	name: string
	type: ContentType
	playCount?: number
	slideCount?: number
	sectionCount?: number
	lastModified: string
	isStarred?: boolean
	style?: React.CSSProperties
	selected?: boolean
	onSelect?: (id: string, type: ContentType) => void
	onRename: (id: string) => void
	onDelete: (id: string) => void
	onDuplicate?: (id: string) => void
	onExport?: (id: string) => void
	onShare?: (id: string) => void
	onToggleStar?: (id: string) => void
}

export function ContentListRow({
								   id,
								   name,
								   type,
								   playCount = 0,
								   slideCount = 0,
								   sectionCount = 0,
								   lastModified,
								   isStarred = false,
								   style,
								   selected,
								   onSelect,
								   onRename,
								   onDelete,
								   onDuplicate,
								   onExport,
								   onShare,
								   onToggleStar,
							   }: ContentListRowProps) {
	const navigate = useNavigate()
	const TypeIcon = typeIcons[type]

	const handleOpen = () => {
		if (type === 'playbook') {
			navigate(`/playbooks/${id}`)
		} else if (type === 'document') {
			navigate(`/documents/${id}/edit`)
		}
	}

	const getItemsText = () => {
		switch (type) {
			case 'playbook':
				return `${playCount} ${playCount === 1 ? 'play' : 'plays'}`
			case 'document':
				return `${playCount} ${playCount === 1 ? 'play' : 'plays'} • ${sectionCount} ${sectionCount === 1 ? 'section' : 'sections'}`
			case 'folder':
			case 'play':
			case 'formation':
			case 'concept':
				return '-'
		}
	}

	const getTypeLabel = () => {
		switch (type) {
			case 'playbook':
				return 'Playbook'
			case 'document':
				return 'Document'
			case 'folder':
				return 'Folder'
			case 'play':
				return 'Play'
			case 'formation':
				return 'Formation'
			case 'concept':
				return 'Concept'
		}
	}

	return (
		<div
			className={`content-list-row ${selected ? 'content-list-row-selected' : ''}`.trim()}
			data-animate="stagger"
			style={style}
		>
			{/* Select button */}
			<div className="content-list-cell" style={{ gridColumn: 'span 1' }}>
				{onSelect && (
					<SelectButton
						selected={selected}
						onClick={() => onSelect(id, type)}
					/>
				)}
			</div>

			{/* Name */}
			<div className="content-list-cell" style={{ gridColumn: 'span 5' }}>
				<button
					onClick={handleOpen}
					className="content-list-name-button"
				>
					{name}
				</button>
			</div>

			{/* Type */}
			<div className="content-list-cell" style={{ gridColumn: 'span 2' }}>
				<div className="content-list-type">
					<TypeIcon size={14} strokeWidth={2} />
					<span>{getTypeLabel()}</span>
				</div>
			</div>

			{/* Items count */}
			<div className="content-list-cell" style={{ gridColumn: 'span 2' }}>
				<span className="content-list-meta">{getItemsText()}</span>
			</div>

			{/* Last modified */}
			<div className="content-list-cell" style={{ gridColumn: 'span 2' }}>
				<span className="content-list-meta">{lastModified}</span>
			</div>

			{/* Actions */}
			<div className="content-list-cell content-list-actions" style={{ gridColumn: 'span 1' }}>
				<div className="content-list-action-buttons">
					{(type === 'playbook' || type === 'document') && (
						<>
							{onToggleStar && (
								<button
									className="content-list-action-button content-list-action-button-star"
									onClick={(e) => {
										e.stopPropagation()
										onToggleStar(id)
									}}
									title={isStarred ? `Unstar ${type === 'document' ? 'Document' : 'Playbook'}` : `Star ${type === 'document' ? 'Document' : 'Playbook'}`}
								>
									<Star
										className={isStarred ? 'content-list-action-icon-starred' : 'content-list-action-icon'}
									/>
								</button>
							)}
							{onShare && (
								<button
									className="content-list-action-button content-list-action-button-share"
									onClick={(e) => {
										e.stopPropagation()
										onShare(id)
									}}
									title={`Share ${type === 'document' ? 'Document' : 'Playbook'}`}
								>
									<Share2 size={16} />
								</button>
							)}
						</>
					)}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<button
								className="content-list-action-button content-list-action-button-more"
								onClick={(e) => {
									e.stopPropagation()
								}}
								title="More options"
							>
								<MoreVertical size={16} />
							</button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
							<DropdownMenuItem onClick={(e) => {
								e.stopPropagation()
								handleOpen()
							}}>
								<BookOpen className="content-list-menu-icon" />
								Open
							</DropdownMenuItem>
							<DropdownMenuItem onClick={(e) => {
								e.stopPropagation()
								onRename(id)
							}}>
								<Edit className="content-list-menu-icon" />
								Rename
							</DropdownMenuItem>
							{onDuplicate && (
								<DropdownMenuItem onClick={(e) => {
									e.stopPropagation()
									onDuplicate(id)
								}}>
									<Copy className="content-list-menu-icon" />
									Duplicate
								</DropdownMenuItem>
							)}
							{onExport && (
								<DropdownMenuItem onClick={(e) => {
									e.stopPropagation()
									onExport(id)
								}}>
									<Download className="content-list-menu-icon" />
									Export
								</DropdownMenuItem>
							)}
							<DropdownMenuSeparator />
							<DropdownMenuItem
								variant="destructive"
								onSelect={(e) => {
									e.preventDefault()
									onDelete(id)
								}}
							>
								<Trash2 className="content-list-menu-icon" />
								Delete
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>
		</div>
	)
}
